// @ts-nocheck

import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaPlus, FaSearch, FaEdit, FaTrash, FaCopy } from "react-icons/fa";

interface StoreDiscountsProps {
  brandId: string;
}

const StoreDiscounts: React.FC<StoreDiscountsProps> = ({ brandId }) => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [discounts, setDiscounts] = useState([
    {
      id: "disc_001",
      code: "SUMMER20",
      type: "percentage",
      value: 20,
      used: 143,
      limit: 500,
      expires: "2026-08-31",
      status: "active",
    },
    {
      id: "disc_002",
      code: "WELCOME10",
      type: "percentage",
      value: 10,
      used: 892,
      limit: null,
      expires: null,
      status: "active",
    },
    {
      id: "disc_003",
      code: "FREESHIP",
      type: "shipping",
      value: 0,
      used: 340,
      limit: 1000,
      expires: "2026-09-15",
      status: "active",
    },
    {
      id: "disc_004",
      code: "SPRING15",
      type: "percentage",
      value: 15,
      used: 500,
      limit: 500,
      expires: "2026-05-01",
      status: "expired",
    },
  ]);

  const handleAddDiscount = () => {
    navigate(`/store/${brandId}/discounts/new`);
  };

  const handleEditDiscount = (discountId: string) => {
    navigate(`/store/${brandId}/discounts/${discountId}`);
  };

  const handleDeleteDiscount = (discountId: string) => {
    setDiscounts(discounts.filter((d) => d.id !== discountId));
  };

  const handleCopyCode = (code: string) => {
    navigator.clipboard?.writeText(code);
  };

  const filteredDiscounts = discounts.filter((d) =>
    d.code.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatValue = (type: string, value: number) => {
    if (type === "percentage") return `${value}% off`;
    if (type === "shipping") return "Free shipping";
    return `$${value} off`;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-white">Discount Codes</h3>
        <button
          onClick={handleAddDiscount}
          className="flex items-center gap-2 rounded bg-blue-600 px-4 py-2 text-white hover:bg-blue-700 transition"
        >
          <FaPlus /> Create Discount
        </button>
      </div>

      <div className="relative">
        <FaSearch className="absolute left-3 top-3 text-gray-500" />
        <input
          type="text"
          placeholder="Search by code..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full rounded bg-gray-800 pl-10 pr-4 py-2 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      <div className="overflow-x-auto rounded bg-gray-900 border border-gray-800">
        <table className="w-full text-sm">
          <thead className="border-b border-gray-800 bg-gray-800">
            <tr>
              <th className="px-6 py-3 text-left font-semibold text-gray-300">Code</th>
              <th className="px-6 py-3 text-left font-semibold text-gray-300">Discount</th>
              <th className="px-6 py-3 text-left font-semibold text-gray-300">Used</th>
              <th className="px-6 py-3 text-left font-semibold text-gray-300">Expires</th>
              <th className="px-6 py-3 text-left font-semibold text-gray-300">Status</th>
              <th className="px-6 py-3 text-center font-semibold text-gray-300">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-800">
            {filteredDiscounts.map((discount) => (
              <tr key={discount.id} className="hover:bg-gray-800 transition">
                <td className="px-6 py-4">
                  <div className="flex items-center gap-2">
                    <span className="font-mono font-medium text-white">{discount.code}</span>
                    <button
                      onClick={() => handleCopyCode(discount.code)}
                      className="text-gray-500 hover:text-gray-300 transition"
                      title="Copy code"
                    >
                      <FaCopy size={12} />
                    </button>
                  </div>
                </td>
                <td className="px-6 py-4 text-gray-300">
                  {formatValue(discount.type, discount.value)}
                </td>
                <td className="px-6 py-4 text-white">
                  {discount.used}
                  {discount.limit ? ` / ${discount.limit}` : ""}
                </td>
                <td className="px-6 py-4 text-gray-400">{discount.expires ?? "No expiry"}</td>
                <td className="px-6 py-4">
                  <span
                    className={`rounded px-2 py-1 text-xs font-semibold ${
                      discount.status === "active"
                        ? "bg-green-900 text-green-300"
                        : "bg-red-900 text-red-300"
                    }`}
                  >
                    {discount.status === "active" ? "Active" : "Expired"}
                  </span>
                </td>
                <td className="px-6 py-4 text-center">
                  <div className="flex items-center justify-center gap-2">
                    <button
                      onClick={() => handleEditDiscount(discount.id)}
                      className="rounded bg-blue-600 p-2 text-white hover:bg-blue-700 transition"
                      title="Edit"
                    >
                      <FaEdit />
                    </button>
                    <button
                      onClick={() => handleDeleteDiscount(discount.id)}
                      className="rounded bg-red-600 p-2 text-white hover:bg-red-700 transition"
                      title="Delete"
                    >
                      <FaTrash />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {filteredDiscounts.length === 0 && (
        <div className="rounded bg-gray-900 border border-gray-800 px-6 py-12 text-center">
          <p className="text-gray-400 mb-4">No discount codes found</p>
          <button
            onClick={handleAddDiscount}
            className="inline-flex items-center gap-2 rounded bg-blue-600 px-4 py-2 text-white hover:bg-blue-700 transition"
          >
            <FaPlus /> Create Your First Discount
          </button>
        </div>
      )}
    </div>
  );
};

export default StoreDiscounts;
